import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { registerInvoice } from '../api/client';

const TAX_CATEGORIES = [
  { id: 1, label: '飲食（8%）　F8' },
  { id: 2, label: '飲食（8%＋10%）　FM' },
  { id: 3, label: '飲食（10%）　F10' },
  { id: 4, label: 'その他経費（10%）　EX' },
];

const getMonthOptions = () => {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`;
  return [
    { value: thisMonth, label: `${now.getFullYear()}年${now.getMonth() + 1}月（当月）` },
    { value: lastMonth, label: `${last.getFullYear()}年${last.getMonth() + 1}月（先月）` },
  ];
};

interface InvoiceRow {
  subject: string;
  amountIncludingTax: string;
  file: File | null;
}

const emptyRow = (): InvoiceRow => ({ subject: '', amountIncludingTax: '', file: null });

export default function InvoicePage() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const monthOptions = getMonthOptions();
  const [targetMonth, setTargetMonth] = useState(monthOptions[0].value);
  const [taxCategoryId, setTaxCategoryId] = useState(4);
  const [rows, setRows] = useState<InvoiceRow[]>([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [successIds, setSuccessIds] = useState<string[]>([]);
  // v0.15: 途中失敗までに提出できた分（完了画面でまとめて表示する）
  const [submittedSoFar, setSubmittedSoFar] = useState<string[]>([]);
  const [error, setError] = useState('');

  if (!auth) {
    window.location.replace('/login');
    return null;
  }

  const updateRow = (i: number, patch: Partial<InvoiceRow>) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = (i: number) => setRows(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!auth) return;
    setError('');
    // 先に全行をチェック（途中まで送ってから止まらないように）
    const bad = rows.findIndex(r => !r.subject || !r.amountIncludingTax || !r.file);
    if (bad >= 0) {
      setError(`請求書 ${bad + 1} の件名・税込金額・ファイルをすべて入力してください`);
      return;
    }
    setSubmitting(true);
    const ids: string[] = [];
    const remaining = [...rows];

    // v0.15: 1件ずつ送り、成功した行は画面から外していく。
    //   途中で失敗しても、成功済みの行をもう一度送って二重提出にならないようにする。
    while (remaining.length > 0) {
      const row = remaining[0];
      const fd = new FormData();
      fd.append('vendorCompanyCode', auth.vendorCompanyCode);
      fd.append('taxCategoryId', String(taxCategoryId));
      fd.append('targetMonth', targetMonth);
      fd.append('amountIncludingTax', row.amountIncludingTax);
      fd.append('subject', row.subject);
      fd.append('files', row.file as File);

      try {
        const res = await registerInvoice(fd);
        ids.push(res.data.fileName);
        remaining.shift();
      } catch (e: any) {
        const status = e.response?.status;
        const msg = e.response?.data?.message ?? 'エラーが発生しました';
        const done = ids.length > 0 ? `${ids.length}件は提出済みです（画面から外しました）。` : '';
        setRows(remaining);
        setSubmittedSoFar(prev => [...prev, ...ids]);
        setError(`${done}「${row.subject}」の提出に失敗しました：${msg}`);
        setSubmitting(false);
        if (status === 401) {
          // ログインの有効期限切れ → ログインし直してもらう（入力中の内容は失われる旨をメッセージで伝える）
          setTimeout(() => { logout(); window.location.replace('/login'); }, 4000);
        }
        return;
      }
    }

    setSuccessIds([...submittedSoFar, ...ids]);
    setSubmittedSoFar([]);
    setSubmitting(false);
  };

  if (successIds.length > 0) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={{ color: '#276749' }}>✓ 請求書を受付しました</h2>
          <p>以下のファイルを保存しました：</p>
          <ul>{successIds.map(id => <li key={id} style={{ fontFamily: 'monospace' }}>{id}</li>)}</ul>
          <p style={{ color: '#718096', fontSize: 13 }}>ご提出ありがとうございました。</p>
          <button style={styles.button} onClick={() => { setSuccessIds([]); setRows([emptyRow()]); }}>
            続けて登録する
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={styles.title}>請求書登録</h1>
          <button style={styles.logoutBtn} onClick={logout}>ログアウト</button>
        </div>
        <p style={styles.info}>{auth?.email} でログイン中</p>

        {/* 共通設定 */}
        <div style={styles.sharedCard}>
          <Field label="対象月 *">
            <select style={styles.input} value={targetMonth} onChange={e => setTargetMonth(e.target.value)}>
              {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="消費税区分 *">
            <select style={styles.input} value={taxCategoryId} onChange={e => setTaxCategoryId(Number(e.target.value))}>
              {TAX_CATEGORIES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        {/* 請求書行 */}
        {rows.map((row, i) => (
          <div key={i} style={styles.rowCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <strong style={{ fontSize: 14 }}>請求書 {i + 1}</strong>
              {rows.length > 1 && (
                <button style={styles.removeBtn} onClick={() => removeRow(i)}>削除</button>
              )}
            </div>
            <Field label="件名 *">
              <input style={styles.input} value={row.subject} onChange={e => updateRow(i, { subject: e.target.value })} />
            </Field>
            <Field label="税込金額（円） *">
              <input
                style={styles.input}
                type="text"
                inputMode="numeric"
                value={row.amountIncludingTax}
                onChange={e => updateRow(i, { amountIncludingTax: e.target.value.replace(/\D/g, '') })}
                placeholder="100000"
              />
            </Field>
            <Field label="請求書ファイル *">
              <input
                type="file"
                accept=".pdf,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg"
                onChange={e => updateRow(i, { file: e.target.files?.[0] ?? null })}
              />
              <p style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                PDF / Excel / Word / 画像に対応。PDF以外はそのまま保存されます。
              </p>
              {row.file && (
                <p style={{ fontSize: 13, marginTop: 4, color: '#4a5568' }}>選択中: {row.file.name}</p>
              )}
            </Field>
          </div>
        ))}

        <button style={styles.addBtn} onClick={addRow}>＋ 別の請求書を追加</button>

        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.button} onClick={handleSubmit} disabled={submitting}>
          {submitting ? '送信中...' : '請求書を提出する'}
        </button>

        <div style={styles.linkSection}>
          <button style={styles.linkBtn} onClick={() => navigate('/inquiry')}>
            お問い合わせ
          </button>
          <span style={{ color: '#cbd5e0' }}>|</span>
          <button style={styles.linkBtn} onClick={() => navigate('/change-contact')}>
            担当者変更はこちら
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 13, color: '#4a5568' }}>{label}</label>
      {children}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f0f4f8', padding: '24px 16px' },
  card: { background: '#fff', borderRadius: 12, padding: 28, maxWidth: 640, margin: '0 auto', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  title: { margin: 0, fontSize: 20, color: '#1a365d' },
  info: { background: '#ebf8ff', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#2c5282', marginBottom: 16 },
  sharedCard: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 },
  rowCard: { border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  button: { width: '100%', padding: 14, background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, cursor: 'pointer', fontWeight: 600, marginTop: 8 },
  addBtn: { width: '100%', padding: 10, background: '#fff', color: '#2b6cb0', border: '1px dashed #2b6cb0', borderRadius: 6, fontSize: 14, cursor: 'pointer', marginBottom: 12 },
  removeBtn: { padding: '4px 10px', background: '#fff', color: '#e53e3e', border: '1px solid #e53e3e', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  logoutBtn: { padding: '6px 14px', background: '#fff', color: '#718096', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  error: { color: '#e53e3e', fontSize: 14, marginTop: 8 },
  linkSection: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' },
  linkBtn: { background: 'none', border: 'none', color: '#2b6cb0', fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: 0 },
};
